import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { BindingContext, Subscribable } from 'knockout';
import { NodeTracker } from 'managers/nodes/tracker';
import { EffectsContainer } from 'mixins/withEffects';
import './item.html';
import './item.scss';

export enum GridItemState {
    justCreated = 0,
    pending = 1,
    rendered = 2,
    active = 3,
    hidden = 4
}

const focusedGridItem = ko.observable<GridItemVM>();

const hiddenStates = <Array<GridItemState>>[
    GridItemState.justCreated,
    GridItemState.pending,
    GridItemState.hidden
]

export interface GridItemModelConfig<T> {
    data: T
    index: Subscribable<number>
    animate: Subscribable<boolean>
}

export class GridItemModel<T = any> {
    private node = ko.observable<HTMLElement>();
    private isDisposed = ko.observable(false);

    vm = ko.observable<GridItemVM<T>>();

    height = ko.observable(0).extend({ notifyIfChanged: true });
    columnIdx = ko.observable<number>();
    attachedTo = ko.observable<number>();

    state = ko.observable(GridItemState.justCreated);
    initialized = ko.observable(false);
    animated = ko.observable(false);

    index: Subscribable<number>
    data: Subscribable<T>

    animate: Subscribable<boolean>
    hasContent: Subscribable<boolean>

    constructor(jsonModel: GridItemModelConfig<T>) {
        this.index = ko.flattenComputed(jsonModel.index);
        this.data = ko.observable(jsonModel.data);

        this.animate = ko.observable(jsonModel.animate).unwrap().default(true);

        this.hasContent = ko.pureComputed(() => this.height() > 0);
    }

    update() {
        this.vm.invokeNotNull(vm => vm.update());
    }

    insertBefore(container: HTMLElement, sibling: GridItemModel<T> | undefined, gridContext: BindingContext) {
        if (this.isDisposed()) return;

        let node = this.node();
        const ref = sibling?.node() ?? null;

        if (node == undefined) {
            node = this.createNode();

            container.insertBefore(node, ref);
            ko.applyBindings(gridContext.createChildContext(this), node);

            this.node(node);
        } else if (ref == undefined && !container.contains(node) || ref != undefined && node.nextSibling != ref) {
            container.insertBefore(node, ref);
            this.animated(false);
        }

        _.delay(() => this.update(), 100);
    }

    dispose() {
        this.isDisposed(true);
        this.node.invokeNotNull(node => ko.removeNode(node));
        this.node(undefined);
    }

    private createNode() {
        return $('<grid-new-item-sync params="model: $data"></grid-new-item>')[0];
    }
}

@injectable()
export default class GridItemVM<T = any> {
    private tracker: NodeTracker

    data: Subscribable<T>
    animate: Subscribable<boolean>    

    constructor(
        @inject(wccModules.componentConfig) model: GridItemModel<T>,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        this.data = model.data;

        const animated = model.animated;
        const animationAllowed = ko.pureComputed(() => false && model.animate() && !animated());
        this.animate = ko.pureComputed(() => animationAllowed() && !isHidden());

        const state = model.state;

        this.tracker = new NodeTracker({ node: <HTMLElement>info.element });
        const height = this.tracker.height;

        const hasContent = ko.pureComputed(() => height() > 0);
        const isHidden = ko.pureComputed(() => !hasContent() || hiddenStates.includes(state()));
        const isFocused = focusedGridItem.is(this);
        const isInitalized = ko.pureComputed(() => !animationAllowed() || animated());

        requestAnimationFrame(() => {
            effects.register(() => {
                model.vm(this);
                return () => model.vm(undefined);
            });

            effects.register([
                ko.computed(() => model.initialized(isInitalized())),
                ko.computed(() => model.height(height()))
            ]);

            effects.register((_state, hasContent, isInitalized) => {
                if (hasContent) {
                    if (_state == GridItemState.pending)
                        state(GridItemState.rendered);
                    else if (isInitalized && _state == GridItemState.rendered)
                        state(GridItemState.active);
                }
            }, [state, hasContent, isInitalized]);
        });

        ko.applyBindingsToNode(info.element, {
            'animationComplete': () => animated(true),

            'css': {
                'grid-new__item': true,
                'grid-new__item--focused': isFocused,
                'grid-new__item--hidden': isHidden,
                'grid-new__item--empty': hasContent.not()
            },

            'click': () => this.onClick()
        });

        this.tracker.update();
    }

    update() {
        this.tracker.update(true);
    }

    private focus() {
        focusedGridItem(this);
    }

    private onClick() {
        this.focus();
        return true;
    }
}